import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DataTable
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Calendar } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/format';

// Mock IKEA resale orders data
const mockOrders = [
  {
    id: "RSO-BILLY001",
    customer: {
      name: "John Smith",
      email: "john.smith@email.com"
    },
    items: [
      {
        id: "BILLY-80263832",
        name: "BILLY Bookcase",
        price: 49.99,
        condition: "Good"
      }
    ],
    status: "confirmed",
    total: 49.99,
    createdAt: "2024-03-15",
    shippingAddress: "123 Main St, City, Country",
    paymentMethod: "credit_card"
  },
  {
    id: "RSO-POANG002",
    customer: {
      name: "Emma Johnson",
      email: "emma.j@email.com"
    },
    items: [
      {
        id: "POANG-89129308",
        name: "POÄNG Armchair",
        price: 89.99,
        condition: "Excellent"
      }
    ],
    status: "shipped",
    total: 89.99,
    createdAt: "2024-03-14",
    shippingAddress: "456 Oak Ave, Town, Country",
    paymentMethod: "paypal"
  },
  {
    id: "RSO-MALM003",
    customer: {
      name: "Michael Brown",
      email: "m.brown@email.com"
    },
    items: [
      {
        id: "MALM-89129307",
        name: "MALM Bed Frame",
        price: 159.99,
        condition: "Good"
      }
    ],
    status: "delivered",
    total: 159.99,
    createdAt: "2024-03-13",
    shippingAddress: "789 Pine Rd, Village, Country",
    paymentMethod: "credit_card"
  },
  {
    id: "RSO-KALLAX004",
    customer: {
      name: "Sarah Wilson",
      email: "sarah.w@email.com"
    },
    items: [
      {
        id: "KALLAX-80275887",
        name: "KALLAX Shelf Unit",
        price: 69.99,
        condition: "Fair"
      }
    ],
    status: "pending",
    total: 69.99,
    createdAt: "2024-03-12",
    shippingAddress: "321 Elm St, City, Country",
    paymentMethod: "credit_card"
  },
  {
    id: "RSO-LACK005",
    customer: {
      name: "David Miller",
      email: "d.miller@email.com"
    },
    items: [
      {
        id: "LACK-30449908",
        name: "LACK Side Table",
        price: 7.99,
        condition: "Good"
      }
    ],
    status: "cancelled",
    total: 7.99,
    createdAt: "2024-03-11",
    shippingAddress: "654 Maple Dr, Town, Country",
    paymentMethod: "paypal"
  }
];

const dateRanges = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '180d', label: 'Last 180 days' },
  { value: '365d', label: 'Last 365 days' },
];

const orderStatuses = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const columns = [
  {
    accessorKey: 'id',
    header: 'Order ID',
    cell: ({ row }) => (
      <Link
        to={`/admin/re-commerce/resale/orders/${row.original.id}`}
        className="font-medium hover:underline"
      >
        {row.original.id}
      </Link>
    ),
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.customer.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.customer.email}</div>
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status.toLowerCase();
      const variant = {
        pending: 'outline',
        confirmed: 'secondary',
        shipped: 'default',
        delivered: 'default',
        cancelled: 'destructive',
      }[status] || 'default';

      return (
        <Badge variant={variant}>
          {row.original.status}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'total',
    header: 'Total',
    cell: ({ row }) => formatCurrency(row.original.total),
  },
  {
    accessorKey: 'items',
    header: 'Items',
    cell: ({ row }) => (
      <div>
        {row.original.items.map(item => (
          <div key={item.id} className="text-sm">
            {item.name} ({item.condition})
          </div>
        ))}
      </div>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Order Date',
    cell: ({ row }) => formatDate(row.original.createdAt),
  },
  {
    accessorKey: 'actions',
    header: '',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>View Details</DropdownMenuItem>
          <DropdownMenuItem>Update Status</DropdownMenuItem>
          <DropdownMenuItem>Contact Customer</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
];

export function ResaleOrdersPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    status: 'all',
    dateRange: '30d',
  });

  // Filter orders based on current filters
  const filteredOrders = React.useMemo(() => {
    return mockOrders.filter(order => {
      const matchesSearch =
        order.id.toLowerCase().includes(filters.search.toLowerCase()) ||
        order.customer.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        order.customer.email.toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus =
        filters.status === 'all' ||
        order.status.toLowerCase() === filters.status.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="p-6">
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search orders..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
              >
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {orderStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <Select
                  value={filters.dateRange}
                  onValueChange={(value) => handleFilterChange('dateRange', value)}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dateRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={filteredOrders}
            emptyMessage="No orders found"
            loadingMessage="Loading orders..."
          />
        </CardContent>
      </Card>
    </div>
  );
}
